import { useRef, useEffect, useState } from 'react';
import type { SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { GarminAiAttribution } from '../components/GarminAttribution.tsx';
import { useStore } from '../lib/store.ts';
import { useChat } from '../hooks/useChat.ts';
import { usePlan } from '../hooks/usePlan.ts';
import { showPaywall } from '../lib/plan.ts';
import { useNoRides } from '../hooks/useSetup.ts';
import { NoRidesCallout } from '../components/setup/NoRidesCallout.tsx';
import { useConversations, useConversation, useDeleteConversation } from '../hooks/useConversations.ts';
import type { ConversationSummary } from '../hooks/useConversations.ts';

export function Chat() {
  const { t } = useTranslation();
  const { chatHistory, clearChat, conversationId, setConversationId, addMessage } = useStore();
  const { send, cancel, isStreaming, loadingText } = useChat();
  const { isFree, freeChatLeft, freeChatMonthly } = usePlan();
  const noRides = useNoRides();
  const [input, setInput] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const { data: conversations } = useConversations();
  const { data: loadedConvo } = useConversation(
    conversationId && chatHistory.length === 0 ? conversationId : null
  );
  const deleteConvo = useDeleteConversation();

  // Load conversation messages when switching
  useEffect(() => {
    if (loadedConvo?.messages && chatHistory.length === 0) {
      for (const msg of loadedConvo.messages) {
        addMessage({
          role: msg.role === 'assistant' ? 'coach' : 'user',
          content: msg.content,
          aiSources: msg.role === 'assistant' ? loadedConvo.ai_sources : undefined,
        });
      }
    }
  }, [loadedConvo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Smooth scroll to bottom as tokens arrive
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory, isStreaming]);

  // Update tab title while streaming
  useEffect(() => {
    if (isStreaming) {
      document.title = `\u{1F6B4} ${t('chat.typing')}`;
    } else {
      document.title = t('chat.title');
    }
    return () => { document.title = t('chat.title'); };
  }, [isStreaming, t]);

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    const msg = input.trim();
    if (!msg || isStreaming) return;
    setInput('');
    send(msg);
  };

  const handleNewChat = () => {
    cancel();
    clearChat();
    setSidebarOpen(false);
  };

  const handleSelectConvo = (convo: ConversationSummary) => {
    cancel();
    clearChat();
    setConversationId(convo.id);
    setSidebarOpen(false);
  };

  const handleDeleteConvo = (id: number) => {
    if (!globalThis.confirm(t('chat.confirmDeleteConversation'))) return;
    deleteConvo.mutate(id);
    if (conversationId === id) {
      cancel();
      clearChat();
    }
  };

  return (
    <div className="flex h-full relative">
      {/* Sidebar toggle (mobile) */}
      <button
        onClick={() => setSidebarOpen((s) => !s)}
        className="md:hidden absolute top-3 left-2 z-20 text-(--color-text-secondary) hover:text-(--color-text-primary) p-1"
        title={t('chat.toggleConversations')}
        aria-label={t('chat.toggleConversations')}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>

      {/* Conversation sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        } absolute md:relative z-10 w-64 h-full bg-(--color-bg-secondary) border-r border-(--color-border) flex flex-col transition-transform md:flex shrink-0`}
      >
        <div className="p-3 border-b border-(--color-border)">
          <button
            onClick={handleNewChat}
            className="w-full py-2 rounded-lg text-sm font-medium bg-(--color-accent)/15 text-(--color-accent) hover:bg-(--color-accent)/25 transition-colors"
          >
            {t('chat.newChat')}
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {conversations?.map((c) => (
            <div
              key={c.id}
              className={`group flex items-center gap-2 px-3 py-2.5 cursor-pointer border-b border-(--color-border)/50 hover:bg-(--color-bg-tertiary) transition-colors ${
                conversationId === c.id ? 'bg-(--color-accent)/10' : ''
              }`}
            >
              <button
                onClick={() => handleSelectConvo(c)}
                className="flex-1 text-left min-w-0"
              >
                <p className="text-sm text-(--color-text-primary) truncate">{c.title}</p>
                <p className="text-xs text-(--color-text-secondary)">
                  {new Date(c.updated_at).toLocaleDateString()} · {t('chat.messageCount', { count: c.message_count })}
                </p>
              </button>
              <button
                onClick={() => handleDeleteConvo(c.id)}
                className="md:opacity-0 md:group-hover:opacity-100 focus-visible:opacity-100 text-(--color-text-secondary) hover:text-red-400 text-xs px-1 transition-opacity"
                title={t('chat.deleteConversation')}
                aria-label={t('chat.deleteConversation')}
              >
                X
              </button>
            </div>
          ))}
          {conversations?.length === 0 && (
            <p className="text-xs text-(--color-text-secondary) text-center py-4">{t('chat.noConversations')}</p>
          )}
        </div>
      </aside>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <button
          type="button"
          aria-label={t('chat.closeSidebar')}
          className="md:hidden fixed inset-0 bg-black/40 z-[5] cursor-default"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main chat area */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-(--color-border) bg-(--color-bg-secondary)">
          <div className="flex items-center gap-2 ml-8 md:ml-0">
            <span className="text-xl">{'\u{1F6B4}'}</span>
            <h1 className="text-lg font-bold">{t('chat.title')}</h1>
          </div>
          {chatHistory.length > 0 && (
            <button
              onClick={handleNewChat}
              className="text-xs text-(--color-text-secondary) hover:text-(--color-text-primary) px-2 py-1 rounded border border-(--color-border) hover:bg-(--color-bg-tertiary) transition-colors"
            >
              {t('chat.clearChat')}
            </button>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {chatHistory.length === 0 && !isStreaming && (
            <div className="flex flex-col items-center justify-center h-full text-center text-(--color-text-secondary)">
              <span className="text-6xl mb-4">{'\u{1F6B4}'}</span>
              <h2 className="text-xl font-semibold text-(--color-text-primary) mb-2">
                {t('chat.welcome')}
              </h2>
              <p className="max-w-md">
                {t('chat.welcomeSub')}
              </p>
            </div>
          )}
          {chatHistory.map((msg, i) => {
            const isLastCoach = msg.role === 'coach' && i === chatHistory.length - 1 && isStreaming;
            return (
              <div key={`${msg.role}-${i}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    msg.role === 'user'
                      ? 'bg-green-400 text-gray-900 rounded-br-md'
                      : 'bg-(--color-bg-tertiary) text-(--color-text-primary) rounded-bl-md'
                  }`}
                >
                  {msg.role === 'coach' && <span className="text-base mr-1">{'\u{1F6B4}'}</span>}
                  {msg.content}
                  {isLastCoach && <span className="inline-block w-0.5 h-4 bg-(--color-accent) ml-0.5 align-middle animate-pulse" />}
                  {msg.role === 'coach' && !isLastCoach && (
                    <GarminAiAttribution sources={msg.aiSources} className="mt-2" />
                  )}
                </div>
              </div>
            );
          })}
          {isStreaming && loadingText && (
            <div className="flex justify-start">
              <div className="bg-(--color-bg-tertiary) px-4 py-3 rounded-2xl rounded-bl-md text-sm text-(--color-text-secondary)">
                {loadingText}
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSubmit} className="px-4 py-3 border-t border-(--color-border) bg-(--color-bg-secondary)">
          {noRides && <NoRidesCallout variant="chat" />}
          {isFree && freeChatLeft !== null && (
            <div className="max-w-3xl mx-auto mb-2 flex items-center justify-between gap-2 rounded-lg bg-(--color-accent)/10 px-3 py-2 text-xs">
              <span>{t('plan.freeLeft', { left: freeChatLeft, total: freeChatMonthly ?? freeChatLeft })}</span>
              <button type="button" onClick={() => showPaywall()} className="font-semibold text-(--color-accent) hover:underline">
                {t('plan.getPro')}
              </button>
            </div>
          )}
          <div className="flex gap-2 max-w-3xl mx-auto">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={isStreaming ? t('chat.responding') : t('chat.placeholder')}
              disabled={isStreaming}
              className="flex-1 bg-(--color-bg-tertiary) text-(--color-text-primary) placeholder-(--color-text-secondary) rounded-xl px-4 py-3 text-sm border border-(--color-border) focus:outline-none focus:border-(--color-accent) transition-colors disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={isStreaming || !input.trim()}
              className="bg-green-400 text-gray-900 px-5 py-3 rounded-xl font-semibold text-sm hover:bg-green-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {t('chat.send')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
